import { useEffect, useRef, useState } from "react";
import {
  currentBuildId,
  fetchServerBuildId,
  isOutdated,
  VERSION_POLL_MS,
} from "../lib/appVersion";

/**
 * Полоска «вышло обновление» и автоматическое обновление вкладки.
 *
 * Раньше после выкладки человек продолжал работать на старой версии, пока
 * сам не нажмёт Ctrl+F5, и не видел свежих правок. Теперь вкладка узнаёт
 * о новой сборке и предлагает обновиться, а если человек отвлёкся и вернулся
 * к вкладке — обновляется сама (набор текста при этом не прерываем).
 */

function isBusyTyping(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || Boolean(el.closest(".cm-editor"));
}

export function AppUpdateBanner() {
  const [outdated, setOutdated] = useState(false);
  const build = useRef(currentBuildId()).current;

  useEffect(() => {
    if (!build) return;
    let stopped = false;

    const check = async () => {
      if (stopped || document.visibilityState === "hidden") return;
      const server = await fetchServerBuildId();
      if (!stopped && isOutdated(build, server)) setOutdated(true);
    };

    void check();
    const timer = window.setInterval(() => void check(), VERSION_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [build]);

  useEffect(() => {
    if (!outdated) return;
    // Человек ушёл на другую вкладку и вернулся — незаметно обновляем,
    // но не посреди набора текста.
    const onVisible = () => {
      if (document.visibilityState === "visible" && !isBusyTyping()) {
        window.location.reload();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [outdated]);

  if (!outdated) return null;

  return (
    <div className="wb-app-update" role="status">
      <span className="wb-app-update__text">Вышло обновление доски</span>
      <button
        type="button"
        className="wb-app-update__btn"
        onClick={() => window.location.reload()}
      >
        Обновить
      </button>
    </div>
  );
}
